import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { AppContext } from "../../context";
import {
  createStockItems,
  getStockAllGroups,
  getStockDashboardGroups,
} from "../production/service";
import { toErrorText } from "./storeSalesDashboardHelpers";
import "./StoreSalesDashboard.css";

const STOCK_DASHBOARD_TABS = [
  { value: "stocking_groups", label: "Stocking Groups" },
  { value: "all_groups", label: "All Groups" },
];

const StoreStockDashboard = () => {
  const { currentUser } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("stocking_groups");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allGroups, setAllGroups] = useState([]);
  const [allGroupsLoading, setAllGroupsLoading] = useState(false);
  const [allGroupsError, setAllGroupsError] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [quantity, setQuantity] = useState("1");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const pillClassName = useMemo(
    () => (value) =>
      `btn btn-sm rounded-pill ${
        activeTab === value ? "btn-primary" : "btn-light border text-dark"
      }`,
    [activeTab]
  );

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await getStockDashboardGroups();
      setGroups(rows);
    } catch (err) {
      setGroups([]);
      setError(toErrorText(err, "Unable to load stocked groups."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllGroups = useCallback(async () => {
    setAllGroupsLoading(true);
    setAllGroupsError(null);
    try {
      const rows = await getStockAllGroups();
      setAllGroups(rows);
    } catch (err) {
      setAllGroups([]);
      setAllGroupsError(toErrorText(err, "Unable to load all groups."));
    } finally {
      setAllGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (activeTab !== "all_groups") return;
    loadAllGroups();
  }, [activeTab, loadAllGroups]);

  const openAddModal = (group) => {
    setSelectedGroup(group);
    setQuantity("1");
    setActionError(null);
    setSuccessMessage(null);
  };

  const closeAddModal = () => {
    if (actionLoading) return;
    setSelectedGroup(null);
    setQuantity("1");
    setActionError(null);
  };

  const handleSubmit = async () => {
    if (!selectedGroup) return;

    const parsedQuantity = Number(quantity);
    if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
      setActionError("Quantity must be a positive integer.");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      const groupLabel = selectedGroup.m_label;
      await createStockItems({
        m_key: selectedGroup.m_key,
        quantity: parsedQuantity,
      });
      closeAddModal();
      setSuccessMessage(
        `Created ${parsedQuantity} stock item(s) for ${groupLabel}.`
      );
      await loadGroups();
    } catch (err) {
      setActionError(toErrorText(err, "Unable to create stock items."));
    } finally {
      setActionLoading(false);
    }
  };

  if (!currentUser?.admin) {
    if (currentUser) {
      return (
        <div className="min-vh-100 w-100 mw-100 bg-light">
          <div className="container-fluid py-4 px-3 px-lg-4 w-100 mw-100">
            <Alert color="danger" fade={false}>
              UNAUTHORIZED
            </Alert>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div className="min-vh-100 w-100 mw-100 bg-light">
      <div className="container-fluid py-4 px-3 px-lg-4 w-100 mw-100">
        <h1 className="h4 fw-bold mb-4">Stock Dashboard</h1>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {STOCK_DASHBOARD_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={pillClassName(tab.value)}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "stocking_groups" && successMessage && (
          <Alert color="success" fade={false} className="mb-3">
            {successMessage}
          </Alert>
        )}

        <div className="store-dashboard-tab-panel rounded border p-3">
          {activeTab === "stocking_groups" ? (
            <>
              {error && (
                <Alert color="danger" fade={false}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <div className="py-5 text-center text-muted">
                  <Spinner className="me-2" />
                  Loading…
                </div>
              ) : (
                <div className="table-responsive">
                  <Table
                    bordered
                    hover
                    responsive
                    className="mb-0 store-sales-table"
                  >
                    <thead className="store-sales-table__head">
                      <tr>
                        <th>M Label</th>
                        <th>Group Key</th>
                        <th>In Stock</th>
                        <th>Demand</th>
                        <th>Balance</th>
                        <th style={{ width: "8rem" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-muted text-center py-4"
                          >
                            No stocked groups found.
                          </td>
                        </tr>
                      ) : (
                        groups.map((group) => (
                          <tr key={group.m_key}>
                            <td>{group.m_label}</td>
                            <td>{group.m_key}</td>
                            <td>{group.in_stock}</td>
                            <td>{group.demand}</td>
                            <td>{group.balance}</td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => openAddModal(group)}
                              >
                                Add
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          ) : (
            <>
              {allGroupsError && (
                <Alert color="danger" fade={false}>
                  {allGroupsError}
                </Alert>
              )}

              {allGroupsLoading ? (
                <div className="py-5 text-center text-muted">
                  <Spinner className="me-2" />
                  Loading…
                </div>
              ) : (
                <div className="table-responsive">
                  <Table
                    bordered
                    hover
                    responsive
                    className="mb-0 store-sales-table"
                  >
                    <thead className="store-sales-table__head">
                      <tr>
                        <th>Group key</th>
                        <th>Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allGroups.length === 0 ? (
                        <tr>
                          <td
                            colSpan={2}
                            className="text-muted text-center py-4"
                          >
                            No groups found.
                          </td>
                        </tr>
                      ) : (
                        allGroups.map((group) => (
                          <tr key={group.m_key}>
                            <td>{group.m_key}</td>
                            <td>{group.used}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>

        <Modal isOpen={selectedGroup != null} toggle={closeAddModal} centered>
          <ModalHeader toggle={closeAddModal}>
            Add stock items — {selectedGroup ? selectedGroup.m_label : ""}
          </ModalHeader>
          <ModalBody>
            {actionError && (
              <Alert color="danger" fade={false}>
                {actionError}
              </Alert>
            )}
            <label htmlFor="stock-quantity-input" className="form-label mb-2">
              Quantity
            </label>
            <Input
              id="stock-quantity-input"
              type="number"
              min="1"
              step="1"
              value={quantity}
              disabled={actionLoading}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={closeAddModal}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={actionLoading}
            >
              Submit
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default StoreStockDashboard;
